/**
 * Sticky navbar —— 移植自中台 cornerstone 的 sticky-navbar
 * 去除 Bootstrap 依赖：collapse / scrollspy 均为原生实现
 */
class StickyNavbar {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.nav = wrapper.querySelector('.sticky-navbar__nav');
    this.toggle = wrapper.querySelector('.sticky-navbar__toggle');
    this.backdrop = wrapper.querySelector('.sticky-navbar-backdrop');
    this.tabs = Array.from(
      wrapper.querySelectorAll('.sticky-navbar__link[data-analytics-name], .sticky-navbar__link[data-section-number]')
    );
    this.main = wrapper.parentElement;
    this.sections = this.main ? Array.from(this.main.children) : [];
    this.pcQuery = window.matchMedia('(min-width: 1024px)');
    this.targets = [];
    this.activeTab = null;
    this.isSuppressing = false;
    // 点击后滚动期间锁住高亮的 tab（null 表示未锁定），以及滚动停稳的防抖计时器
    this.clickLockTab = null;
    this.settleTimer = null;

    this.resolveAnchors();
    this.bindEvents();
    this.updateSpy();
    this.syncHeaderSuppression();
  }

  // 每个 tab 解析出滚动目标，并把 href 指向该目标所在的 .shopify-section（原生 hash 跳转依然可用）
  resolveAnchors() {
    this.tabs.forEach((tab) => {
      const target = this.resolveTarget(tab);
      if (!target || target === this.wrapper) return;
      const section = target.closest('.shopify-section');
      if (section === this.wrapper) return;
      const anchorId = (section || target).id;
      if (!anchorId) return;
      tab.setAttribute('href', `#${anchorId}`);
      this.targets.push({ tab, target });
    });
  }

  // 优先按中台的 analytics_name 找页面上同名 id 的元素（即目标 section 的 section_id）；
  // 名称为空、或名称找不到时回退到 section 序号（与中台 handleSectionId 逻辑一致）
  resolveTarget(tab) {
    const name = (tab.dataset.analyticsName || '').trim();
    if (name) {
      const named = document.getElementById(name);
      if (named) return named;
    }
    return this.sectionByNumber(tab.dataset.sectionNumber);
  }

  sectionByNumber(value) {
    const number = Number(value);
    if (!number || number < 1 || number > this.sections.length) return null;
    return this.sections[number - 1] || null;
  }

  bindEvents() {
    if (this.toggle) {
      this.toggle.addEventListener('click', () => this.toggleCollapse());
    }

    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.hideCollapse());
    }

    // 点击锚点平滑滚动，移动端点击后收起面板
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', (event) => {
        const target = this.clickTarget(tab);
        if (!target) return;
        event.preventDefault();
        // 立即点亮被点的 tab，并在滚动停稳前锁住高亮：
        // 点击后纵向滚动刚起步的一两帧里，滚动位置还在原模块区间内，updateSpy
        // 会算出「还是上一个模块」而把高亮翻回去（实测约 20ms 后发生），
        // 表现为刚点亮的那个 tab 闪一下。锁定期间不跑 updateSpy 的高亮更新，
        // 滚动停稳后再解锁并按真实落点校正一次。
        // recenter: false —— 横向居中同样交给停稳后统一做，避免点击时先滚再被拽回。
        if (this.targets.some((item) => item.tab === tab)) {
          this.clickLockTab = tab;
          this.applyActive(tab, { recenter: false });
        }
        this.scrollToTarget(target);
        this.hideCollapse();
      });
    });

    // 窗口变化：切到 PC 时强制收起移动端面板
    this.pcQuery.addEventListener('change', (event) => {
      if (event.matches) this.hideCollapse();
    });

    // 滚动时：高亮当前区块对应的 tab
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        // 锁定期间（点击后滚动中）不更新高亮，否则会把刚点亮的 tab 翻回上一个模块。
        // 其余情况：只更新高亮不做横向居中（recenter: false），滚动中 active 会在
        // 两个模块间反复翻转，每次都居中会让轨道被来回推；居中留到停稳后做一次。
        if (!this.clickLockTab) this.updateSpy({ recenter: false });
        this.syncHeaderSuppression();
        ticking = false;
        this.scheduleSettle();
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  // 滚动停稳（连续 150ms 无新滚动）后收尾：解锁高亮锁 + 把激活 tab 横向居中一次
  scheduleSettle() {
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => {
      if (this.clickLockTab) {
        this.clickLockTab = null;
        // 解锁后按真实落点校正一次高亮（recenter 默认 true，顺带完成横向居中）
        this.updateSpy();
        return;
      }
      this.centerActiveTab();
    }, 150);
  }

  // tab 点击时的滚动目标：优先用解析结果；Entry 之类的站内锚点再用 href 兜底
  clickTarget(tab) {
    const entry = this.targets.find((item) => item.tab === tab);
    if (entry) return entry.target;
    const href = tab.getAttribute('href');
    if (!href || !href.startsWith('#')) return null;
    return document.getElementById(href.slice(1));
  }

  // 滚动落点与高亮判定线共用同一个偏移量：目标所在 .shopify-section 的
  // scroll-margin-top（定义在 sticky-navbar.css，当前为 0：目标 section 顶对齐 navbar 上边缘）
  scrollOffset(target) {
    const section = target && (target.closest('.shopify-section') || target);
    const margin = section ? parseFloat(getComputedStyle(section).scrollMarginTop) : NaN;
    // 不能用 `margin || ...` 兜底：scroll-margin-top 就是 0 时是 falsy，
    // 会被误判成"没定义"而退回 navbar 高度，导致落点与判定线错开一段
    return Number.isNaN(margin) ? this.wrapper.offsetHeight + 1 : margin;
  }

  scrollToTarget(target) {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const top = target.getBoundingClientRect().top + scrollTop - this.scrollOffset(target);
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  updateSpy({ recenter = true } = {}) {
    if (this.targets.length === 0) return;
    const offset = this.scrollOffset(this.targets[0].target);
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const line = scrollTop + offset;
    let active = null;
    let activeTop = -Infinity;

    // 判定线落在哪个模块的范围内，哪个 tab 高亮：
    //   进入条件 = 目标元素顶部越过判定线（与点击后的落点一致）
    //   离开条件 = 判定线已经超过目标所在 .shopify-section 的底部
    // 越过模块后不再把高亮留给上一个 tab；处在没配 tab 的模块里则是全部不高亮。
    //
    // 两个容错，都是为了修「点 tab 滚到位后胶囊还停在上一个 tab」：
    // 1. TOLERANCE：点击落点让 targetTop 与判定线理论上相等，但 smooth 滚动会停在
    //    整数像素上，元素顶部却是小数（如 4243 vs 4243.4），差不到 1px 就会让
    //    `targetTop <= line` 判假，高亮留在上一个模块。放宽 2px 吃掉这个误差。
    // 2. 取「最靠下的命中项」而不是「最后遍历到的命中项」：相邻 section 的
    //    bottom 与下一个 top 会重合，一个 scrollTop 可能同时落在两个区间里，
    //    此时应当选更靠下的那个，而不是依赖 targets 的遍历顺序。
    const TOLERANCE = 2;

    this.targets.forEach(({ tab, target }) => {
      const targetTop = target.getBoundingClientRect().top + scrollTop;
      const section = target.closest('.shopify-section') || target;
      const sectionBottom = section.getBoundingClientRect().bottom + scrollTop;
      if (targetTop <= line + TOLERANCE && line < sectionBottom && targetTop > activeTop) {
        active = tab;
        activeTop = targetTop;
      }
    });

    this.applyActive(active, { recenter });
  }

  // 把高亮落到某个 tab 上（tab 为 null 则全部取消）。
  // 点击和滚动监听共用，避免两处各写一份状态同步逻辑。
  // recenter: 是否顺带把横向轨道滚到该 tab 居中。点击时传 false（见 click 处理），
  // 居中只由滚动路径负责，避免点击与滚动监听对轨道做方向相反的两次横滚。
  applyActive(tab, { recenter = true } = {}) {
    this.targets.forEach(({ tab: item }) => {
      item.classList.toggle('active', item === tab);
    });

    // 高亮没变也要尊重 recenter：点已激活的 tab 时用户仍期望它滚回中间
    if (tab === this.activeTab) {
      if (recenter && tab) this.centerActiveTab();
      return;
    }
    this.activeTab = tab;
    if (recenter) this.centerActiveTab();
  }

  // 移动端平铺滚动模式下，把激活的 tab 滚到轨道中间（与中台 multiple-item-tab 的做法一致）。
  // 只有轨道真的能横向滚动时才动作，所以折叠菜单模式下天然是空操作。
  centerActiveTab() {
    const track = this.nav;
    if (!track || !this.activeTab) return;
    if (track.scrollWidth <= track.clientWidth) return;

    const trackRect = track.getBoundingClientRect();
    const tabRect = this.activeTab.getBoundingClientRect();
    const delta = tabRect.left + tabRect.width / 2 - (trackRect.left + trackRect.width / 2);
    if (Math.abs(delta) < 1) return;

    track.scrollBy({ left: delta, behavior: 'smooth' });
  }

  // 规则：navbar 顶部与视口顶部的间距 < header 高度时，header 不允许出现。
  // 吸顶时间距为 0，天然落入此判定；未吸顶但贴近顶部时同样成立，
  // 所以不需要再单独判断「是否吸顶」。
  syncHeaderSuppression() {
    const gap = this.wrapper.getBoundingClientRect().top;
    const headerHeight =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const suppress = gap < headerHeight;
    if (suppress === this.isSuppressing) return;
    this.isSuppressing = suppress;
    document.body.classList.toggle('sticky-navbar-stuck', suppress);
  }

  toggleCollapse() {
    this.wrapper.classList.toggle('show');
    document.body.classList.toggle('sticky-navbar-open', this.wrapper.classList.contains('show'));
  }

  hideCollapse() {
    this.wrapper.classList.remove('show');
    document.body.classList.remove('sticky-navbar-open');
  }
}

function initStickyNavbar() {
  document.querySelectorAll('.sticky-navbar-section').forEach((wrapper) => {
    if (wrapper.dataset.stickyNavbarInit) return;
    wrapper.dataset.stickyNavbarInit = 'true';
    new StickyNavbar(wrapper);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStickyNavbar);
} else {
  initStickyNavbar();
}

// Shopify 主题编辑器内重新加载 section 时重新初始化
document.addEventListener('shopify:section:load', initStickyNavbar);

// 主题的 instant-page 局部换页后，如果新页面没有 sticky navbar，清掉遗留的 body 类
document.addEventListener('page:loaded', () => {
  if (document.querySelector('.sticky-navbar-section')) return;
  document.body.classList.remove('sticky-navbar-open');
  document.body.classList.remove('sticky-navbar-stuck');
});
